import type { ReactNode } from 'react';
import { OTP_LENGTH } from '@/lib/config';

/* ── Text field ─────────────────────────────────────────────────────────── */
export function Field({
  id,
  label,
  hint,
  error,
  prefix,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  prefix?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium">
        {label}
      </label>

      <div
        className="flex items-stretch overflow-hidden rounded-xl border transition-all duration-150 focus-within:ring-4"
        style={{
          background: 'var(--surface)',
          borderColor: error ? 'var(--danger)' : 'var(--line)',
          // @ts-expect-error custom property for the focus ring colour
          '--tw-ring-color': error ? 'var(--danger-soft)' : 'var(--marigold-ring)',
        }}
      >
        {prefix && (
          <span
            className="flex shrink-0 items-center border-r px-3.5 text-sm font-medium text-[var(--ink-2)]"
            style={{ background: 'var(--surface-2)', borderColor: 'var(--line)' }}
          >
            {prefix}
          </span>
        )}
        {children}
      </div>

      {error ? (
        <p className="mt-1.5 text-xs" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-[var(--muted)]">{hint}</p>
      ) : null}
    </div>
  );
}

export const inputClass =
  'w-full bg-transparent px-3.5 py-3 text-[15px] outline-none placeholder:text-[var(--muted)]';

/* ── OTP boxes ──────────────────────────────────────────────────────────── */
export function OtpInput({
  value,
  onChange,
  length = OTP_LENGTH,
}: {
  value: string;
  onChange: (v: string) => void;
  length?: number;
}) {
  return (
    <div className="relative">
      {/* One real input behind the boxes — keeps mobile keyboards and paste working */}
      <input
        id="otp"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={length}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, length))}
        className="absolute inset-0 z-10 size-full cursor-pointer opacity-0"
        aria-label="Verification code"
      />
      <div className="flex gap-2">
        {Array.from({ length }, (_, i) => {
          const filled = i < value.length;
          const next = i === value.length;
          return (
            <div
              key={i}
              className="flex h-13 flex-1 items-center justify-center rounded-xl border font-[family-name:var(--font-mono)] text-xl font-semibold transition-all duration-150 tabular-nums"
              style={{
                background: 'var(--surface)',
                borderColor: next ? 'var(--marigold)' : 'var(--line)',
                boxShadow: next ? '0 0 0 4px var(--marigold-ring)' : 'none',
              }}
            >
              {filled ? value[i] : ''}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Divider ────────────────────────────────────────────────────────────── */
export function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-px flex-1" style={{ background: 'var(--line)' }} />
      <span className="text-xs text-[var(--muted)]">{label}</span>
      <span className="h-px flex-1" style={{ background: 'var(--line)' }} />
    </div>
  );
}
